import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import PageHero from "../components/PageHero";
import CtaBand from "../components/CtaBand";
import heroBlog from "../assets/heroes/hero-blog.webp";
import ctaStudio from "../assets/heroes/cta-studio.webp";
import { PAGES } from "../seoContent";
import { fetchPosts, urlFor } from "../sanity";

const dateFmt = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

export function PostCard({ post }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group flex flex-col overflow-hidden rounded-3xl border border-black/10 bg-white shadow-[0_16px_40px_rgba(0,0,0,0.06)] transition duration-300 hover:-translate-y-1 hover:border-pink-300 hover:shadow-[0_24px_50px_rgba(236,72,153,0.15)]"
    >
      <div className="aspect-[4/3] overflow-hidden bg-pink-50">
        {post.heroImage?.asset && (
          <img
            src={urlFor(post.heroImage).width(800).auto("format").url()}
            alt={post.heroImage.alt || ""}
            loading="lazy"
            className="h-full w-full object-cover object-top transition duration-700 group-hover:scale-105"
          />
        )}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-pink-600">
          {post.kind === "story" ? "Shoot story" : "Guide"}
          {post.category ? ` · ${post.category}` : ""}
        </p>
        <h2 className="mt-2.5 text-lg font-semibold leading-snug text-black">
          {post.title}
        </h2>
        <p className="mt-2 line-clamp-2 flex-1 text-pretty text-sm leading-relaxed text-black/65">
          {post.excerpt}
        </p>
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-black/50">
            {post.publishedAt ? dateFmt.format(new Date(post.publishedAt)) : ""}
          </span>
          <span className="inline-flex items-center gap-2 font-semibold text-pink-600 transition group-hover:gap-3">
            Read
            <ArrowRight size={16} aria-hidden="true" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function Blog() {
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchPosts()
      .then((data) => alive && setPosts(data || []))
      .catch((err) => {
        console.error("Failed to load articles:", err);
        if (alive) setError(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <>
      <SEO title={PAGES["/blog"].title} description={PAGES["/blog"].description} />
      <main className="min-h-screen bg-white text-black">
        <PageHero
          title={PAGES["/blog"].h1}
          image={heroBlog}
          imagePosition="center"
        />

        <section className="bg-[#fff7fb]">
          <div className="mx-auto max-w-7xl px-6 py-14 sm:px-10 md:py-20 lg:px-16 xl:px-10">
            {posts === null && !error ? (
              <p className="text-center text-black/60">Loading articles...</p>
            ) : error ? (
              <div className="mx-auto max-w-xl rounded-3xl border border-black/10 bg-white p-8 text-center">
                <p className="text-black/70">
                  We could not load the articles just now. Please refresh the page or try again later.
                </p>
              </div>
            ) : posts.length === 0 ? (
              <div className="mx-auto max-w-xl rounded-3xl border border-black/10 bg-white p-8 text-center">
                <h2 className="text-2xl font-semibold">New articles are on the way</h2>
                <p className="mt-3 text-black/65">
                  We are putting together photography guides and stories from our shoots. In the meantime, take a look at our work.
                </p>
                <Link
                  to="/gallery"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-pink-500 px-7 py-3 text-[15px] font-semibold text-white shadow-[0_8px_20px_rgba(0,0,0,0.18)] transition hover:-translate-y-0.5 hover:bg-pink-600 active:scale-95"
                >
                  View Our Gallery
                  <ArrowRight size={18} aria-hidden="true" />
                </Link>
              </div>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {posts.map((post) => (
                  <PostCard key={post.slug} post={post} />
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="bg-white">
          <div className="mx-auto max-w-7xl px-6 py-14 sm:px-10 md:py-16 lg:px-16 xl:px-10">
            <CtaBand
              heading="Book Your Studio Photoshoot in Rochdale"
              text="Choose a session, pick a time and get portraits, family and maternity photos you will love."
              to="/book"
              label="Book a Studio Session"
              image={ctaStudio}
            />
          </div>
        </section>
      </main>
    </>
  );
}
